import {inject, Injectable, PLATFORM_ID} from '@angular/core';
import {DOCUMENT, isPlatformBrowser} from '@angular/common';
import {environment} from '../../environments/environment';

declare const window: any;
const SCRIPT_ID = 'google-recaptcha-v3';

@Injectable({
  providedIn: 'root'
})
export class RecaptchaService {

  readonly document = inject(DOCUMENT);
  readonly platformId = inject(PLATFORM_ID);

  private scriptPromise: Promise<void> | null = null;

  private get siteKey(): string {
    return environment.recaptchaSiteKey || '';
  }

  get enabled(): boolean {
    return isPlatformBrowser(this.platformId) && !!this.siteKey;
  }

  async execute(action: string): Promise<string> {
    if (!this.enabled) {
      return '';
    }

    try {
      await this.loadScript();

      return await new Promise<string>((resolve, reject) => {
        window.grecaptcha.ready(() => {
          window.grecaptcha.execute(this.siteKey, {action}).then(resolve, reject);
        });
      });
    } catch {
      return '';
    }
  }

  private loadScript(): Promise<void> {
    if (!this.enabled) {
      return Promise.resolve();
    }

    if (this.scriptPromise) {
      return this.scriptPromise;
    }

    this.scriptPromise = new Promise<void>((resolve, reject) => {
      const existing = this.document.getElementById(SCRIPT_ID) as HTMLScriptElement | null;
      if (existing) {
        resolve();
        return;
      }

      const script = this.document.createElement('script');
      script.id = SCRIPT_ID;
      script.src = `https://www.google.com/recaptcha/api.js?render=${this.siteKey}`;
      script.async = true;
      script.defer = true;
      script.onload = () => resolve();
      script.onerror = () => {
        this.scriptPromise = null;
        reject(new Error('Failed to load reCAPTCHA script'));
      };
      this.document.head.appendChild(script);
    });

    return this.scriptPromise;
  }
}
